import clsx from 'clsx';
import { graphql } from 'gatsby';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { renderHeading, renderParagraph, RichTextRenderer } from '~components/RichTextRenderer';
import { Section } from '~components/Section';
import { renderEmbeddedBlockEntry, renderEmbeddedInlineEntry } from './renderers';
import { getCenterAlignedNodeClassNames, getMedia, getOrderClassName } from './utils';

import * as styles from './ComponentTextMedia.module.css';

import type { ContentBlock } from '~components/types';
import type { ContentfulComponentTextMedia } from './types';

type ComponentTextMediaProps = {
  content: ContentfulComponentTextMedia;
  className?: string;
};

export const ComponentTextMedia: ContentBlock<ComponentTextMediaProps> = ({ content, className, ...sectionProps }) => {
  const media = getMedia(content);

  return (
    <Section
      {...sectionProps}
      className={clsx(
        styles.section,
        media ? styles.double : [styles.single, styles.text, ...getCenterAlignedNodeClassNames(content.centerAlign)],
        getOrderClassName(content.order),
        content.logoInBackground && styles.logoInBackground,
        className,
      )}
    >
      <RichTextRenderer
        content={content.text.raw}
        wrapperElement={media ? 'div' : undefined}
        className={clsx(styles.text, ...getCenterAlignedNodeClassNames(content.centerAlign))}
        options={{
          renderNode: {
            [BLOCKS.HEADING_2]: renderHeading(1, 2, { className: styles.h2 }),
            [BLOCKS.HEADING_3]: renderHeading(2, 3, { className: styles.h3 }),
            [BLOCKS.HEADING_4]: renderHeading(3, 4, { className: styles.h4 }),
            [BLOCKS.PARAGRAPH]: renderParagraph({ className: styles.p }),
            [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedBlockEntry(content.text.references),
            [INLINES.EMBEDDED_ENTRY]: renderEmbeddedInlineEntry(content.text.references),
          },
        }}
      />
      {media && <div className={clsx(styles.media, content.stickyMedia && styles.sticky)}>{media}</div>}
    </Section>
  );
};

export const query = graphql`
  fragment ComponentTextMedia on ContentfulComponentTextMedia {
    __typename
    text {
      raw
      references {
        ...ComponentLink
      }
    }
    centerAlign
    media {
      ...MixedMedia
    }
    youTubeVideoLink
    links {
      ...ComponentLink
    }
    order
    theme
    blockLayout
    logoInBackground
    stickyMedia
  }
`;
